<template>
  <el-dialog
    :visible.sync="visible"
    :title="`Cyanite Analysis - ${track && track.title ? track.title : ''}`"
    width="800px"
    custom-class="premium-modal cyanite-modal"
    @close="$emit('close')"
  >
    <div v-if="track && track.cyanite && hasData" class="cyanite-content">
      <div class="analysis-grid">
        <!-- BPM & Key -->
        <div class="analysis-section" v-if="track.cyanite.bpm || track.cyanite.key_type">
          <h4><i class="el-icon-timer"></i> Technical Details</h4>
          <div class="detail-items">
            <div class="detail-item" v-if="track.cyanite.bpm">
              <span class="label">BPM:</span>
              <el-tag type="primary">{{ track.cyanite.bpm }}</el-tag>
            </div>
            <div class="detail-item" v-if="track.cyanite.key_type">
              <span class="label">Key:</span>
              <el-tag type="info">{{ formatKey(track.cyanite.key_type) }}</el-tag>
            </div>
          </div>
        </div>

        <!-- Genres -->
        <div class="analysis-section" v-if="track.cyanite.genres && track.cyanite.genres.length">
          <h4><i class="el-icon-collection-tag"></i> Genres</h4>
          <div class="tag-cloud">
            <el-tag
              v-for="genre in track.cyanite.genres"
              :key="genre"
              class="genre-tag"
              effect="plain">
              {{ formatGenre(genre) }}
            </el-tag>
          </div>
        </div>

        <!-- Sub Genres -->
        <div class="analysis-section" v-if="track.cyanite.sub_genres && track.cyanite.sub_genres.length">
          <h4><i class="el-icon-price-tag"></i> Sub Genres</h4>
          <div class="tag-cloud">
            <el-tag
              v-for="subgenre in track.cyanite.sub_genres"
              :key="subgenre"
              class="subgenre-tag"
              type="success"
              effect="plain">
              {{ formatGenre(subgenre) }}
            </el-tag>
          </div>
        </div>

        <!-- Moods -->
        <div class="analysis-section" v-if="track.cyanite.moods && track.cyanite.moods.length">
          <h4><i class="el-icon-star-on"></i> Moods</h4>
          <div class="tag-cloud">
            <el-tag
              v-for="mood in track.cyanite.moods"
              :key="mood"
              class="mood-tag"
              type="warning"
              effect="plain">
              {{ capitalize(mood) }}
            </el-tag>
          </div>
        </div>

        <!-- Advanced Moods -->
        <div class="analysis-section" v-if="track.cyanite.advanced_moods && track.cyanite.advanced_moods.length">
          <h4><i class="el-icon-magic-stick"></i> Advanced Moods</h4>
          <div class="tag-cloud">
            <el-tag
              v-for="mood in track.cyanite.advanced_moods"
              :key="mood"
              class="advanced-mood-tag"
              type="danger"
              effect="plain">
              {{ capitalize(mood) }}
            </el-tag>
          </div>
        </div>

        <!-- Character Type -->
        <div class="analysis-section" v-if="track.cyanite.character_type && track.cyanite.character_type.length">
          <h4><i class="el-icon-user"></i> Character</h4>
          <div class="tag-cloud">
            <el-tag
              v-for="character in track.cyanite.character_type"
              :key="character"
              class="character-tag"
              effect="dark">
              {{ capitalize(character) }}
            </el-tag>
          </div>
        </div>

        <!-- Instruments -->
        <div class="analysis-section" v-if="track.cyanite.instruments && track.cyanite.instruments.length">
          <h4><i class="el-icon-headset"></i> Instruments</h4>
          <div class="tag-cloud">
            <el-tag
              v-for="instrument in track.cyanite.instruments"
              :key="instrument"
              class="instrument-tag"
              type="info"
              effect="plain">
              {{ formatInstrument(instrument) }}
            </el-tag>
          </div>
        </div>

        <!-- Voice Gender -->
        <div class="analysis-section" v-if="track.cyanite.voice_gender && track.cyanite.voice_gender.length">
          <h4><i class="el-icon-microphone"></i> Voice</h4>
          <div class="tag-cloud">
            <el-tag
              v-for="voice in track.cyanite.voice_gender"
              :key="voice"
              :type="voice === 'instrumental' ? 'info' : 'primary'">
              {{ capitalize(voice) }}
            </el-tag>
          </div>
        </div>

        <!-- Emotional Profile & Musical Era -->
        <div class="analysis-section" v-if="track.cyanite.emotional_profile || track.cyanite.musical_era">
          <h4><i class="el-icon-data-line"></i> Profile</h4>
          <div class="detail-items">
            <div class="detail-item" v-if="track.cyanite.emotional_profile">
              <span class="label">Emotion:</span>
              <el-tag :type="track.cyanite.emotional_profile === 'positive' ? 'success' : 'warning'">
                {{ capitalize(track.cyanite.emotional_profile) }}
              </el-tag>
            </div>
            <div class="detail-item" v-if="track.cyanite.musical_era">
              <span class="label">Era:</span>
              <el-tag type="info">{{ capitalize(track.cyanite.musical_era) }}</el-tag>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="no-data">
      <i class="el-icon-warning-outline"></i>
      <p>No data available</p>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'CyaniteModal',

  props: {
    visible: {
      type: Boolean,
      default: false
    },
    track: {
      type: Object,
      default: null
    }
  },

  computed: {
    hasData() {
      if (!this.track || !this.track.cyanite) return false;
      const cyanite = this.track.cyanite

      return !!(
        cyanite.bpm ||
        cyanite.key_type ||
        (cyanite.genres && cyanite.genres.length) ||
        (cyanite.sub_genres && cyanite.sub_genres.length) ||
        (cyanite.moods && cyanite.moods.length) ||
        (cyanite.advanced_moods && cyanite.advanced_moods.length) ||
        (cyanite.character_type && cyanite.character_type.length) ||
        (cyanite.instruments && cyanite.instruments.length) ||
        (cyanite.voice_gender && cyanite.voice_gender.length) ||
        cyanite.emotional_profile ||
        cyanite.musical_era
      )
    }
  },

  methods: {
    capitalize(str) {
      if (!str) return ''
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    formatGenre(genre) {
      // Convert camelCase to Title Case
      return genre
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim()
    },

    formatInstrument(instrument) {
      // Convert camelCase to Title Case
      return instrument
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim()
    },

    formatKey(key) {
      // Convert key format (e.g., gMinor -> G Minor)
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim()
    }
  },

  watch: {
    visible(val) {
      if (!val) {
        this.$emit('update:visible', false)
        this.$emit('close')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
// Premium Modal Styles
.premium-modal {
  ::v-deep .el-dialog__header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 20px;

    .el-dialog__title {
      color: white;
      font-size: 18px;
      font-weight: 600;
    }

    .el-dialog__close {
      color: white;
      font-size: 20px;

      &:hover {
        color: #f0f0f0;
      }
    }
  }

  ::v-deep .el-dialog__body {
    padding: 30px;
  }
}

// Cyanite Modal Specific Styles
.cyanite-modal {
  .cyanite-content {
    .analysis-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 25px;
    }

    .analysis-section {
      background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
      border-radius: 12px;
      padding: 20px;
      border: 1px solid #e9ecef;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transform: translateY(-2px);
      }

      h4 {
        margin: 0 0 15px 0;
        color: #495057;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          font-size: 16px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }
      }

      .tag-cloud {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        .el-tag {
          border-radius: 20px;
          font-weight: 500;
          font-size: 12px;
          transition: all 0.2s ease;

          &:hover {
            transform: scale(1.05);
          }
        }

        .genre-tag {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          border: 1px solid rgba(102, 126, 234, 0.2);
        }

        .subgenre-tag {
          background: rgba(103, 194, 58, 0.1);
          color: #67c23a;
          border: 1px solid rgba(103, 194, 58, 0.2);
        }

        .mood-tag {
          background: rgba(230, 162, 60, 0.1);
          color: #e6a23c;
          border: 1px solid rgba(230, 162, 60, 0.2);
        }

        .advanced-mood-tag {
          background: rgba(245, 108, 108, 0.1);
          color: #f56c6c;
          border: 1px solid rgba(245, 108, 108, 0.2);
        }

        .character-tag {
          background: rgba(64, 158, 255, 0.1);
          color: #409eff;
          border: 1px solid rgba(64, 158, 255, 0.2);
        }

        .instrument-tag {
          background: rgba(144, 147, 153, 0.1);
          color: #909399;
          border: 1px solid rgba(144, 147, 153, 0.2);
        }
      }

      .detail-items {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .detail-item {
          display: flex;
          align-items: center;
          gap: 10px;

          .label {
            font-weight: 600;
            color: #606266;
            font-size: 13px;
            min-width: 60px;
          }

          .el-tag {
            font-weight: 500;
          }
        }
      }
    }
  }

  .no-data {
    text-align: center;
    padding: 40px;
    color: #909399;

    i {
      font-size: 48px;
      margin-bottom: 16px;
      color: #c0c4cc;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }
}
</style>